import type { DimensionId, StabilityType, OverallStabilityType } from './types'

// ===== 维度基础信息 =====
export const DIMENSION_INFO: Record<DimensionId, {
  name: string
  description: string
  poleA: { letter: string; name: string; shortDesc: string }
  poleB: { letter: string; name: string; shortDesc: string }
}> = {
  D1: {
    name: '锚定来源',
    description: '你做判断和评价时的参照标准',
    poleA: { letter: 'A', name: '内部锚定', shortDesc: '依据自己的标准' },
    poleB: { letter: 'R', name: '外部锚定', shortDesc: '参考外部反馈' },
  },
  D2: {
    name: '处理模式',
    description: '你处理信息和解决问题的方式',
    poleA: { letter: 'S', name: '系统化', shortDesc: '结构化、逻辑优先' },
    poleB: { letter: 'C', name: '情境化', shortDesc: '灵活应变、直觉优先' },
  },
  D3: {
    name: '驱动来源',
    description: '驱动你行动的主要动力',
    poleA: { letter: 'I', name: '内在驱动', shortDesc: '兴趣和意义感' },
    poleB: { letter: 'E', name: '外在驱动', shortDesc: '结果和认可' },
  },
  D4: {
    name: '不确定性态度',
    description: '你面对不确定性时的倾向',
    poleA: { letter: 'D', name: '闭合取向', shortDesc: '倾向于尽快确定' },
    poleB: { letter: 'O', name: '探索取向', shortDesc: '愿意保持开放' },
  },
}

// ===== 极点详细解读 =====
export type PoleKey = 'D1-A' | 'D1-R' | 'D2-S' | 'D2-C' | 'D3-I' | 'D3-E' | 'D4-D' | 'D4-O'

export interface PoleInterpretation {
  summary: string
  behaviors: string[]
  strengths: string[]
  challenges: string[]
}

export const POLE_INTERPRETATIONS: Record<PoleKey, PoleInterpretation> = {
  'D1-A': {
    summary: '你倾向于依据自己的标准来评价事物和做出判断',
    behaviors: [
      '面对外部批评时，会先分析对方说得对不对，而非直接接受或沮丧',
      '别人的认可让你高兴，但不会改变你对自己的评价',
      '在群体意见与自己不同时，不会因为"大家都这么想"而动摇',
    ],
    strengths: [
      '不易被外界评价左右，内心较为稳定',
      '能够坚持自己认为正确的方向',
    ],
    challenges: [
      '可能忽略有价值的外部反馈',
      '在需要团队共识的场合可能需要更多沟通',
    ],
  },
  'D1-R': {
    summary: '你倾向于参考外部反馈来校准自己的判断和评价',
    behaviors: [
      '重视他人的评价和反馈，会认真考虑并据此调整',
      '外部认可能显著提升你的满足感和自信',
      '在做判断时会考虑"别人怎么看"',
    ],
    strengths: [
      '善于吸收外部信息，能快速适应环境期待',
      '在团队中容易达成共识',
    ],
    challenges: [
      '外部反馈的变化可能影响内心状态',
      '对负面反馈可能比较敏感',
    ],
  },
  'D2-S': {
    summary: '你倾向于用结构化、逻辑化的方式处理信息和解决问题',
    behaviors: [
      '遇到问题时会先分析结构、拆解步骤',
      '喜欢建立框架和标准来评估事物',
      '解释事情时倾向于先说结论，再按逻辑展开',
    ],
    strengths: [
      '处理复杂问题时条理清晰',
      '能建立可复用的方法论和流程',
    ],
    challenges: [
      '可能过度分析，错过需要快速行动的时机',
      '在信息不完整时可能感到不安',
    ],
  },
  'D2-C': {
    summary: '你倾向于根据具体情境灵活应对，依靠直觉和经验做判断',
    behaviors: [
      '遇到问题时会先感受整体情况，再决定怎么做',
      '善于读取人际氛围和情境线索',
      '能根据不同场合灵活调整方式',
    ],
    strengths: [
      '反应灵活，适应性强',
      '在信息模糊时仍能做出判断',
    ],
    challenges: [
      '处理复杂系统性问题时可能需要额外梳理',
      '经验依赖可能导致在陌生领域不适应',
    ],
  },
  'D3-I': {
    summary: '你的行动主要由内在兴趣、好奇心和意义感驱动',
    behaviors: [
      '愿意投入时间做"没用但有意思"的事',
      '即使没有外部反馈，也能持续投入自己认为有价值的事',
      '做事的满足感主要来自过程本身',
    ],
    strengths: [
      '能长期坚持自己热爱的事',
      '不易被短期利益牵引',
    ],
    challenges: [
      '可能较少关注外部回报和他人期待',
      '在需要明确产出的环境中可能感到约束',
    ],
  },
  'D3-E': {
    summary: '你的行动较多由目标达成、结果实现和成就认可驱动',
    behaviors: [
      '在意事情的回报和实际价值',
      '外部认可和反馈能显著提升动力',
      '会根据投入产出比来分配精力',
    ],
    strengths: [
      '目标感强，效率导向',
      '善于把握有价值的机会',
    ],
    challenges: [
      '在看不到明确目标或成果时可能需要调整动力来源',
      '可能错过需要长期投入才能见效的事',
    ],
  },
  'D4-D': {
    summary: '你倾向于尽快明确方向、减少不确定性',
    behaviors: [
      '喜欢有明确的计划和目标',
      '面对多个选择时倾向于尽快做决定',
      '计划被打乱时会感到不适',
    ],
    strengths: [
      '执行力强，能快速推进',
      '在需要果断决策的场合表现好',
    ],
    challenges: [
      '可能过早锁定方向，错过更好的可能',
      '面对持续变化的环境时可能感到焦虑',
    ],
  },
  'D4-O': {
    summary: '你愿意保持开放，在不确定中探索可能性',
    behaviors: [
      '不急于做决定，喜欢保留选项',
      '对新的可能性感到兴奋而非焦虑',
      '能接受计划的变化',
    ],
    strengths: [
      '创新性强，愿意尝试',
      '在复杂多变的环境中适应良好',
    ],
    challenges: [
      '可能拖延决策，错过时机',
      '在需要快速确定方向时可能犹豫',
    ],
  },
}

// ===== 稳定性模式解读 =====
export interface StabilityInterpretation {
  name: string
  shortDesc: string
  description: string
}

export const STABILITY_INTERPRETATIONS: Record<StabilityType, StabilityInterpretation> = {
  stable: {
    name: '稳定',
    shortDesc: '跨情境高度一致',
    description: '这是你认知风格的核心特质，在不同情境下都保持一致',
  },
  work_adaptive: {
    name: '工作适应',
    shortDesc: '进入工作环境后调整',
    description: '这是正常的适应能力，你能在工作场合调整行为方式',
  },
  stress_switch: {
    name: '压力切换',
    shortDesc: '日常工作一致，高压下切换',
    description: '可能是应激反应或自我保护机制，高压下会切换到不同的模式',
  },
  gradual: {
    name: '渐进变化',
    shortDesc: '随情境逐步变化',
    description: '情境压力持续影响你的认知模式，呈现渐进式变化',
  },
  flexible: {
    name: '高弹性',
    shortDesc: '跨情境变化明显',
    description: '你的认知风格高度情境敏感，能灵活适应不同环境',
  },
}

// ===== 整体稳定性类型解读 =====
export interface OverallStabilityInterpretation {
  name: string
  summary: string
  description: string
}

export const OVERALL_STABILITY_INTERPRETATIONS: Record<OverallStabilityType, OverallStabilityInterpretation> = {
  core_stable: {
    name: '核心稳定型',
    summary: '你的认知风格跨情境高度一致',
    description: '无论在放松、工作还是压力状态下，你都保持着稳定的认知模式。这意味着你有明确的行为风格，环境变化不太会改变你的核心特质。',
  },
  single_flex: {
    name: '单点弹性型',
    summary: '你有一个主要的适应维度，其他维度保持稳定',
    description: '你的大部分认知特质跨情境稳定，但在某一个维度上展现出适应性。这种局部弹性让你既有稳定的核心特质，又能在特定方面灵活调整。',
  },
  context_adaptive: {
    name: '情境适应型',
    summary: '你能根据不同情境灵活调整认知模式',
    description: '你展现出较强的情境适应能力，能够根据环境需求调整自己的认知方式。这种灵活性在多变的环境中是一种优势。',
  },
  highly_flexible: {
    name: '高度弹性型',
    summary: '你的认知风格高度情境依赖',
    description: '你在不同情境下展现出明显不同的认知模式，适应性很强。这可能意味着你对环境线索非常敏感，能够快速调整以适应不同场合。',
  },
}

// ===== 环境匹配建议 =====
export interface EnvironmentSuggestion {
  fits: string[]
  drains: string[]
}

export const ENVIRONMENT_SUGGESTIONS: Record<PoleKey, EnvironmentSuggestion> = {
  'D1-A': {
    fits: [
      '允许独立判断、不过度依赖外部认可的环境',
      '鼓励个人见解和独立思考的团队',
      '重视专业能力而非人际认可的文化',
    ],
    drains: [
      '过度强调共识和认同的环境',
      '需要频繁寻求他人许可才能行动的场合',
    ],
  },
  'D1-R': {
    fits: [
      '有清晰反馈机制的环境',
      '重视团队共识和协作的文化',
      '定期提供认可和肯定的管理风格',
    ],
    drains: [
      '长期缺乏反馈的独立工作',
      '不重视沟通和认可的环境',
    ],
  },
  'D2-S': {
    fits: [
      '有明确流程和结构化工作方式的环境',
      '重视方法论和标准化的团队',
      '需要处理复杂系统性问题的场合',
    ],
    drains: [
      '混乱无序、缺乏规划的环境',
      '需要频繁即兴应对的场合',
    ],
  },
  'D2-C': {
    fits: [
      '灵活应变、允许即兴发挥的环境',
      '重视人际敏感度和情商的场合',
      '变化快、需要快速适应的行业',
    ],
    drains: [
      '过于僵化、强调流程的环境',
      '需要长期遵循固定模式的工作',
    ],
  },
  'D3-I': {
    fits: [
      '有自主空间、能追求意义感的环境',
      '允许深度投入感兴趣领域的场合',
      '不过分强调短期产出的工作节奏',
    ],
    drains: [
      '纯粹以外部指标衡量价值的环境',
      '缺乏意义感的重复性工作',
    ],
  },
  'D3-E': {
    fits: [
      '有明确激励机制和可见回报路径的环境',
      '绩效导向、目标明确的团队',
      '能够快速看到成果的项目',
    ],
    drains: [
      '长期看不到回报的投入',
      '缺乏明确目标和激励的环境',
    ],
  },
  'D4-D': {
    fits: [
      '目标明确、减少模糊地带的环境',
      '有清晰路径和明确截止日期的项目',
      '决策链清晰、不拖泥带水的团队',
    ],
    drains: [
      '长期不确定、方向模糊的状态',
      '频繁变更计划和目标的环境',
    ],
  },
  'D4-O': {
    fits: [
      '允许试错、任务开放性较高的环境',
      '鼓励创新和探索的团队文化',
      '不急于求成、允许迭代的项目',
    ],
    drains: [
      '过于强调确定性和即时结果的环境',
      '容错空间很小的高压场合',
    ],
  },
}

// ===== 情境切换解读函数 =====
export type SwitchType = 'work' | 'stress'

export interface ContextSwitchInsight {
  type: SwitchType
  fromPole: string
  toPole: string
  insight: string
}

export function getContextSwitchInsight(
  dimension: DimensionId,
  fromPole: string,
  toPole: string,
  switchType: SwitchType
): ContextSwitchInsight {
  const dimInfo = DIMENSION_INFO[dimension]
  const fromName = fromPole === dimInfo.poleA.letter ? dimInfo.poleA.name : dimInfo.poleB.name
  const toName = toPole === dimInfo.poleA.letter ? dimInfo.poleA.name : dimInfo.poleB.name

  const insights: Record<DimensionId, Record<SwitchType, string>> = {
    D1: {
      work: fromPole === 'A'
        ? '在工作中你会更多参考外部标准，这是对职场规则的适应'
        : '在工作中你会更多依据自己的判断，可能是专业自信的体现',
      stress: fromPole === 'A'
        ? '高压下你会更在意外部评价，可能需要关注压力下的自我怀疑'
        : '高压下你会更坚持自己的判断，这是一种自我保护',
    },
    D2: {
      work: fromPole === 'S'
        ? '在工作中你变得更灵活变通，能够适应职场的人际需求'
        : '在工作中你变得更系统化，这是对专业要求的适应',
      stress: fromPole === 'S'
        ? '高压下你会放弃系统分析，转而依赖直觉快速响应'
        : '高压下你会寻求结构和秩序，通过分析来获得控制感',
    },
    D3: {
      work: fromPole === 'I'
        ? '在工作中你会更关注外部激励，这是对职场规则的务实适应'
        : '在工作中你找到了更多内在意义，或能按兴趣选择任务',
      stress: fromPole === 'I'
        ? '高压下你会更关注外部回报，可能是为了获得确定感'
        : '高压下你会回归内在动机，用意义感支撑自己度过难关',
    },
    D4: {
      work: fromPole === 'D'
        ? '在工作中你保持更多开放性，能够适应职场的变化'
        : '在工作中你倾向于更快做决定，适应职场的效率要求',
      stress: fromPole === 'D'
        ? '高压下你变得更开放，可能在寻找新的出路'
        : '高压下你急于锁定方向，希望通过确定性来减少焦虑',
    },
  }

  return {
    type: switchType,
    fromPole,
    toPole,
    insight: `从${fromName}切换到${toName}：${insights[dimension][switchType]}`,
  }
}

// ===== 获取极点解读的便捷函数 =====
export function getPoleInterpretation(dimension: DimensionId, pole: string): PoleInterpretation | null {
  const key = `${dimension}-${pole}` as PoleKey
  return POLE_INTERPRETATIONS[key] || null
}

// ===== 获取环境建议的便捷函数 =====
export function getEnvironmentSuggestion(dimension: DimensionId, pole: string): EnvironmentSuggestion | null {
  const key = `${dimension}-${pole}` as PoleKey
  return ENVIRONMENT_SUGGESTIONS[key] || null
}
